import { create } from 'zustand';
import type {
  LimitErrorCode,
  LimitErrorMeta,
  PaywallPayload,
} from '@/shared/lib/api';

/**
 * Holds the currently visible server-driven limit paywall. The dialog is
 * mounted once at the root; feature code just calls `showPaywall(payload, code)`
 * and the shared `<LimitPaywallDialog />` picks it up.
 *
 * `code` is kept alongside the payload so consumers (e.g. analytics) can tell
 * which limit triggered it without re-parsing the error.
 *
 * `meta` is the limit error's structured metadata (when routed from a 422) —
 * the pack paywalls use `meta.project_id` to bind the offer to the building
 * the limit actually fired on, which can differ from the current route during
 * cross-building flows. Optional: preflight-routed paywalls have no meta and
 * fall back to the route's project.
 */
interface LimitPaywallState {
  code: LimitErrorCode | null;
  payload: PaywallPayload | null;
  meta: LimitErrorMeta | null;
  showPaywall: (
    payload: PaywallPayload,
    code: LimitErrorCode,
    meta?: LimitErrorMeta
  ) => void;
  hidePaywall: () => void;
}

export const useLimitPaywallStore = create<LimitPaywallState>((set) => ({
  code: null,
  payload: null,
  meta: null,
  showPaywall: (payload, code, meta) => set({ payload, code, meta: meta ?? null }),
  hidePaywall: () => set({ payload: null, code: null, meta: null }),
}));
